import { Button, useNotify } from "react-admin";
import {
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  makeStyles,
  TextField,
  Typography,
} from "@material-ui/core";
import React, { useState } from "react";
import { StyledButton } from "../../../components/ListShowButton";
import { useField } from "react-final-form";
import { cloneDeep, find, findIndex } from "lodash";
import SanitizedGrid from "../../../components/SanitizedGrid";
import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  removeSchoolLocationFromRouteLocation,
  reOptimiseRouteLocation,
} from "../tools";
import AiLoading from "./AiLoading";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  autocompleteInput: {
    marginTop: "8px",
    paddingBottom: "3px",
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#EAEAEA",
      },
      "&:hover fieldset": {
        borderColor: "#EAEAEA",
      },
    },
  },
});

interface MoveUnAssignedLocationButtonProps {
  routeLocationId: number;
}

const MoveUnAssignedLocationButton: React.FC<
  MoveUnAssignedLocationButtonProps
> = ({ routeLocationId }) => {
  const [open, setOpen] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [selectedRouteLocation, setSelectedRouteLocation] = useState<any>(null);
  const [selectedRoute, setSelectedRoute] = useState<any>(null);

  const droppedLocationsField = useField("droppedLocations");
  const formRoutesChangedField = useField("formRoutesChanged");
  const routesField = useField("routes");
  const busScheduleField = useField("busSchedule");
  const schoolField = useField("school");
  const maxStopTimeField = useField("maxStopTime");

  const classes = useStyles();
  const notify = useNotify();

  const handleOpenDialog = () => {
    const findRouteLocation = find(droppedLocationsField.input.value, [
      "id",
      routeLocationId,
    ]);
    setSelectedRouteLocation(findRouteLocation);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleMoveUnAssignedLocation = async () => {
    try {
      setIsLoading(true);

      const findSelectedRouteLocationIndex = findIndex(
        droppedLocationsField.input.value,
        ["id", selectedRouteLocation.id],
      );

      if (findSelectedRouteLocationIndex === -1) {
        notify("Unassigned location not found", "error");
        setIsLoading(false);
        return;
      }

      const updatedDroppedLocations = [
        ...droppedLocationsField.input.value.slice(
          0,
          findSelectedRouteLocationIndex,
        ),
        ...droppedLocationsField.input.value.slice(
          findSelectedRouteLocationIndex + 1,
        ),
      ];

      const findSelectedRouteIndex = findIndex(routesField.input.value, [
        "routeName",
        selectedRoute.routeName,
      ]);

      if (findSelectedRouteIndex === -1) {
        notify("Target route not found", "error");
        setIsLoading(false);
        return;
      }

      const updatedRoutes = cloneDeep(routesField.input.value);
      const copiedRoute = updatedRoutes[findSelectedRouteIndex];

      copiedRoute.routeGeometries = [];

      const clonedLocationToInsert = {
        ...selectedRouteLocation,
        id: 0,
      };

      const insertIndex = copiedRoute.routeLocations.length - 1;
      const newRouteLocations = [
        ...copiedRoute.routeLocations.slice(0, insertIndex),
        clonedLocationToInsert,
        ...copiedRoute.routeLocations.slice(insertIndex),
      ];

      const routeLocationWithoutSchool = removeSchoolLocationFromRouteLocation(
        newRouteLocations,
        busScheduleField.input.value.type,
      );

      const optimisedRouteLocations = await reOptimiseRouteLocation(
        schoolField.input.value,
        busScheduleField.input.value,
        maxStopTimeField.input.value,
        routeLocationWithoutSchool,
      );

      copiedRoute.routeLocations = optimisedRouteLocations;

      routesField.input.onChange(updatedRoutes);
      droppedLocationsField.input.onChange(updatedDroppedLocations);
      formRoutesChangedField.input.onChange(true);

      setIsLoading(false);
      handleClose();
    } catch (error) {
      notify("Failed to move unassigned location to the route", "error");
      setIsLoading(false);
    }
  };

  return (
    <>
      <StyledButton variant="outlined" onClick={handleOpenDialog}>
        Move to Route
      </StyledButton>

      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="sm"
      >
        <DialogTitle id="alert-dialog-title">
          Move Unassigned Location {selectedRouteLocation?.address}
        </DialogTitle>
        <DialogContent>
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2">Move to</Typography>
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                className={classes.autocompleteInput}
                freeSolo
                disableClearable
                options={routesField.input.value}
                getOptionLabel={(option: any) => option.routeName}
                renderOption={(option) => {
                  return (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <span>{option.routeName} - </span>
                      <div
                        style={{
                          backgroundColor: option.routeColor?.colorTwo,
                          width: "20px",
                          height: "20px",
                          borderRadius: "5px",
                          marginLeft: "5px",
                        }}
                      />
                    </div>
                  );
                }}
                onChange={(_event, value: any) => {
                  setSelectedRoute(
                    find(routesField.input.value, [
                      "routeName",
                      value.routeName,
                    ]),
                  );
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search Existing Routes"
                    variant="outlined"
                    InputProps={{ ...params.InputProps, type: "search" }}
                  />
                )}
              />
            </Grid>
          </SanitizedGrid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} color="secondary" label="Back" />
          <Button
            onClick={handleMoveUnAssignedLocation}
            color="primary"
            label="Save"
            startIcon={
              isLoading && (
                <CircularProgress size={20} style={{ color: "#FFFFFF" }} />
              )
            }
          />
        </DialogActions>
      </Dialog>

      <Dialog
        open={isLoading}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        maxWidth="md"
      >
        <AiLoading isLoading={isLoading} />
      </Dialog>
    </>
  );
};

export default MoveUnAssignedLocationButton;
